import { useState } from 'react';
import { Gift, ListChecks, DollarSign } from 'lucide-react';
import { CreatePromotionalOrderForm } from '../CreatePromotionalOrderForm';
import PromotionalOrdersList from '../PromotionalOrdersList';
import CommissionsListView from './CommissionsListView';

interface PromotionsSubTabsProps {
  canCreateOrders: boolean;
}

export default function PromotionsSubTabs({ canCreateOrders }: PromotionsSubTabsProps) {
  const [activeSubTab, setActiveSubTab] = useState<'create' | 'offers' | 'commissions'>('create');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleOfferCreated = () => {
    setRefreshKey(prev => prev + 1);
  };

  const subTabs = [
    { id: 'create' as const, icon: Gift, label: 'Create Offer', badge: null },
    { id: 'offers' as const, icon: ListChecks, label: 'My Offers', badge: null },
    { id: 'commissions' as const, icon: DollarSign, label: 'My Commissions', badge: null }
  ];

  return (
    <div className="space-y-6">
      {/* Sub-Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                  activeSubTab === tab.id
                    ? 'border-rose-500 text-rose-600 bg-rose-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-rose-100 text-rose-600">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Sub-Tab Content */}
        <div className="p-4 sm:p-6">
          {activeSubTab === 'create' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Create Promotional Offer</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Create exclusive offers for specific users with custom discounts and bonuses
                  </p>
                </div>
              </div>
              
              {canCreateOrders ? (
                <>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-lg hover:from-rose-600 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                  >
                    <Gift className="w-5 h-5" />
                    <span>Create New Promotional Offer</span>
                  </button>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                    <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">1.</span>
                        <span>Select a user and plan to create a personalized offer</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">2.</span>
                        <span>Set a discounted price and add bonus features (extra contacts, extra validity)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">3.</span>
                        <span>The offer will automatically appear to the user as a popup</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">4.</span>
                        <span>When they complete payment, you earn commission on the sale</span>
                      </li>
                    </ul>
                  </div>
                </>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <p className="text-yellow-800 font-medium">
                    You do not have permission to create promotional offers.
                  </p>
                  <p className="text-yellow-700 text-sm mt-2">
                    Please contact your administrator to enable the "Create Offers" permission for your account.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeSubTab === 'offers' && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">My Promotional Offers</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Track all promotional offers you've created
                </p>
              </div>
              <PromotionalOrdersList key={refreshKey} />
            </div>
          )}

          {activeSubTab === 'commissions' && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">My Commissions</h3>
                <p className="text-sm text-gray-600 mt-1">
                  View commissions earned from completed promotional orders
                </p>
              </div>
              <CommissionsListView />
            </div>
          )}
        </div>
      </div>

      {/* Create Promotional Order Modal */}
      <CreatePromotionalOrderForm
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleOfferCreated}
      />
    </div>
  );
}